import React, { useEffect, useState, useRef, useContext } from "react";
import { Box, Typography, List, ListItem, ListItemText, Avatar } from "@mui/material";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { startStream } from "./../../action/stream";
import { addSongAction } from "./../../action/songs";
import ApiService from "./../../services/api-service";
import { truncate } from "./../../constants/function";
import Music from "./../../assets/common/resources/default/Music.svg";
import { keyframes } from "@mui/system";
import { SocketContext } from "../../context/socket";
import cookieStorage from "../../services/cookie-storage";
import LiveStreamSnapshot from "./../LiveStreamSnapshot";
import AdSenseAd from "../AdSenseAd";
import AdSenseConfig from "../../config/adsense.config";
import AIBadge from "../AIBadge";
import MonetizationChart from "./MonetizationChart";

const SidebarRight = () => {
  const socket = useContext(SocketContext).Socket;

  // State to store songs, live streams, playlists, featured artist, etc.
  const [topSongs, setTopSongs] = useState([]);
  const [liveStreams, setLiveStreams] = useState([]);
  const [topPlaylists, setTopPlaylists] = useState([]);
  const [featuredArtist, setFeaturedArtist] = useState(null);

  const history = useHistory();
  const dispatch = useDispatch();

  // We'll track the current streams in a ref for real-time updates
  const streamRef = useRef([]);

  // HUD-style pulse animation for live indicator
  const pulseAnimation = keyframes`
    0% { transform: scale(1); opacity: 0.5; }
    50% { transform: scale(1.3); opacity: 1; }
    100% { transform: scale(1); opacity: 0.5; }
  `;

  // ==============================
  //  useEffect: fetch initial data
  // ==============================
  useEffect(() => {
    getNewReleaseSongs();
    getLiveStreams();
    mostStreamedPlaylist();
    getFeaturedArtist();

    // Socket events for new or ended streams
    // Update live streams every 30 seconds
    const interval = setInterval(() => {
      getLiveStreams();
    }, 30000);

    if (socket) {
      socket.on("stream-joined", handleStreamJoined);
      socket.on("stream-ended", handleStreamEnded);
    }

    return () => {
      clearInterval(interval);
      if (socket) {
        socket.off("stream-joined", handleStreamJoined);
        socket.off("stream-ended", handleStreamEnded);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  // ==============================
  //  API calls
  // ==============================
  const getNewReleaseSongs = async () => {
    try {
      const response = await ApiService.MUSIC_NEW_RELEASE();
      if (response?.result?.length) {
        setTopSongs(response.result.slice(0, 4));
      } else {
        setTopSongs([]);
      }
    } catch (error) {
      console.error("Error fetching new release songs:", error);
      setTopSongs([]);
    }
  };

  const mostStreamedPlaylist = async () => {
    try {
      const response = await ApiService.PLAYLIST_STREAMED_LASTWEEK();
      const playlists = response?.result ? response.result.slice(0, 4) : [];
      setTopPlaylists(playlists);
    } catch (error) {
      console.error("Error fetching playlists:", error);
      setTopPlaylists([]);
    }
  };

  const getLiveStreams = async () => {
    try {
      const response = await ApiService.LIVESTREAM();
  
      // Convert your stream objects into a simpler shape for UI
      const streams = response?.result
        ? response.result.slice(0, 4).map((stream) => ({
            id: stream.channelName,
            title: stream.title || "Live Stream",
            creatorId: stream.createdById || "",
            creator: `${stream.createdByDetails.firstName || ""} ${stream.createdByDetails.lastName || ""}`.trim(),
            thumbnail: stream.GIF?.filePath || stream.thumbnailImg?.filePath || Music,
          }))
        : [];

        setLiveStreams(streams);
      streamRef.current = streams;
    } catch (error) {
      console.error("Error fetching live streams:", error);
      setLiveStreams([]);
    }
  };

  const getFeaturedArtist = async () => {
    try {
      const response = await ApiService.FEATURE_ARTIST();
      if (response?.result && response.result.length > 0) {
        setFeaturedArtist(response.result[0]);
      } else {
        setFeaturedArtist(null);
      }
    } catch (error) {
      console.error("Error fetching featured artist:", error);
      setFeaturedArtist(null);
    }
  };

  // ==============================
  //  Socket event handlers
  // ==============================
  const handleStreamJoined = (streamData) => {
    if (!streamData || !streamData.livestreamData) return;
    const newStream = streamData.livestreamData;
    console.log("[socket stream-joined] streamData:", streamData);

    // If the new stream isn't in our list, add it
    if (!streamRef.current.some((stream) => stream.id === newStream.channelName)) {
      const updatedStreams = [
        {
          id: newStream.channelName,
          title: newStream.title || "Live Stream",
          creatorId: newStream.createdById || "",
          creator: newStream.createdBy || "Unknown Creator",
          thumbnail: newStream.GIF?.filePath || newStream.thumbnailImg?.filePath || Music,
        },
        ...streamRef.current,
      ];
      setLiveStreams(updatedStreams);
      streamRef.current = updatedStreams;
    }
  };

  const handleStreamEnded = (data) => {
    if (!data?.channelName) return;
    console.log("[socket stream-ended]", data.channelName);

    const updatedStreams = streamRef.current.filter((stream) => stream.id !== data.channelName);
    setLiveStreams(updatedStreams);
    streamRef.current = updatedStreams;
  };

  // ==============================
  //  handlePlayStream
  // ==============================
  const handlePlayStream = async (channelName) => {
    try {
      console.log("User wants to join existing stream =>", channelName);

      // (optional) fetch additional playlist info
      const playlistResponse = await ApiService.PLAYLIST_DETAIL({
        _id: channelName,
        pageNo: 1,
        pageSize: 1,
      });

      // Body for join
      const finalPayload = {
        channelName,
        platform: "hedera",
      };

      // Call your REST API to join
      const joinResp = await ApiService.LIVEJOIN(finalPayload);
      console.log("[handlePlayStream] After LIVEJOIN =>", joinResp);

      // We expect top-level fields like { token, channelName, ... }
      if (joinResp?.token && joinResp?.channelName) {
        console.log("[handlePlayStream] LIVEJOIN success =>", joinResp);

        // If we want to store playlist info in Redux
        if (playlistResponse?.result) {
          dispatch(startStream({ streamData: playlistResponse.result }));
        }

        // The route we want to push
        const newPath = `/app/LiveVideoStreaming/${channelName}`;
        console.log("[handlePlayStream] Navigating to =>", newPath);

        // Actually navigate
        history.push(newPath);
      } else {
        console.error("[handlePlayStream] LIVEJOIN failed:", joinResp);
      }
    } catch (error) {
      console.error("[handlePlayStream] Error joining stream:", error);
    }
  };

  // For normal audio songs
  const handlePlaySong = (musicItem) => {
    dispatch(addSongAction(musicItem));
  };

  // ==============================
  //  JSX
  // ==============================
  return (
    <Box
      sx={{
        backgroundColor: "var(--hud-sidebar-bg, #08080C)",
        minHeight: "100vh",
        width: "100%",
        padding: "16px 14px 50px 14px",
        color: "var(--hud-text, #E8E8EE)",
        marginTop: 0,
        overflowY: "auto",
        height: "100%",
        borderRadius: 0,
      }}
    >
      {/* MONETIZATION */}
      <MonetizationChart />

      {/* NOW LIVE */}
      {liveStreams.length > 0 && (
        <Box mb={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontFamily: "var(--font-body)", fontWeight: 700, fontSize: "9px", color: "var(--hud-text-muted, #9A9AB0)", letterSpacing: "3px", textTransform: "uppercase" }}>
              Now Live
            </Typography>
            <Typography
              sx={{
                fontFamily: "var(--font-body)",
                color: "var(--hud-accent, #FFEE20)",
                fontSize: "9px",
                letterSpacing: "1px",
                textTransform: "none",
                cursor: "pointer",
                opacity: 0.6,
                transition: "opacity 0.2s ease",
                "&:hover": { opacity: 1 },
              }}
              onClick={() => {
                history.push("/app/home");
              }}
            >
              Explore More
            </Typography>
          </Box>
          <List>
            {liveStreams.map((stream) => (
              <ListItem
                key={stream.id}
                sx={{
                  pl: 1.5,
                  py: 0.8,
                  borderRadius: "4px",
                  backgroundColor: "transparent",
                  border: "1px solid var(--hud-border, #16161F)",
                  mb: 1,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "rgba(255, 238, 32,0.15)",
                    backgroundColor: "rgba(255, 238, 32,0.03)",
                  },
                }}
              >
                <Avatar
                  src={stream.thumbnail}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "4px",
                    mr: 1.5,
                    cursor: "pointer",
                    border: "1px solid var(--hud-border, #16161F)",
                  }}
                  onClick={() => handlePlayStream(stream.id)}
                />
                <ListItemText
                  primary={truncate(stream.title, 12)}
                  secondary={stream.creator}
                  primaryTypographyProps={{ fontFamily: "var(--font-body)", fontSize: "12px", fontWeight: 600, color: "var(--hud-text, #E8E8EE)" }}
                  secondaryTypographyProps={{ fontFamily: "var(--font-body)", color: "var(--hud-text-muted, #9A9AB0)", fontSize: "10px" }}
                  onClick={() => handlePlayStream(stream.id)}
                />
                          {/* Pulsating live dot */}
          <Box
            sx={{
              position: "absolute",
              right: 10,
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: "var(--hud-accent, #FFEE20)",
              animation: `${pulseAnimation} 1.5s infinite`,
              boxShadow: "0 0 8px rgba(255, 238, 32,0.4)",
            }}
          />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* NEW RELEASES */}
      <Box mb={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography sx={{ fontFamily: "var(--font-body)", fontWeight: 700, fontSize: "9px", color: "var(--hud-text-muted, #9A9AB0)", letterSpacing: "3px", textTransform: "uppercase" }}>
            New Releases
          </Typography>
          <Typography
            sx={{
              fontFamily: "var(--font-body)",
              color: "var(--hud-accent, #FFEE20)",
              fontSize: "9px",
              letterSpacing: "1px",
              textTransform: "none",
              cursor: "pointer",
              opacity: 0.6,
              transition: "opacity 0.2s ease",
              "&:hover": { opacity: 1 },
            }}
            onClick={() => {
              console.log("[SidebarRight] Going to => /app/feature/NewRelease");
              history.push("/app/feature/NewRelease");
            }}
          >
            Explore More
          </Typography>
        </Box>
        <List>
          {topSongs.map((song) => (
            <ListItem
              key={song._id}
              sx={{
                pl: 1.5,
                py: 0.8,
                borderRadius: "4px",
                backgroundColor: "transparent",
                border: "1px solid var(--hud-border, #16161F)",
                mb: 1,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "rgba(255, 238, 32,0.15)",
                  backgroundColor: "rgba(255, 238, 32,0.03)",
                },
              }}
            >
              <Avatar
                src={song.thumbnailImg?.filePath}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "4px",
                  mr: 1.5,
                  cursor: "pointer",
                  border: "1px solid var(--hud-border, #16161F)",
                }}
                onClick={() => handlePlaySong(song)}
              />
              <ListItemText
                primary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: "4px", overflow: "hidden" }}>
                    <Typography sx={{ fontFamily: "var(--font-body)", fontSize: "12px", fontWeight: 600, color: "var(--hud-text, #E8E8EE)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {song.title}
                    </Typography>
                    <AIBadge show={song.isAIGenerated} size="small" />
                  </Box>
                }
                secondary={song.artistData?.[0]?.fullName || "Unknown Artist"}
                secondaryTypographyProps={{ fontFamily: "var(--font-body)", color: "var(--hud-text-muted, #9A9AB0)", fontSize: "10px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                onClick={() => handlePlaySong(song)}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* TRENDING PLAYLISTS */}
      <Box mb={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography sx={{ fontFamily: "var(--font-body)", fontWeight: 700, fontSize: "9px", color: "var(--hud-text-muted, #9A9AB0)", letterSpacing: "3px", textTransform: "uppercase" }}>
            Trending Playlists
          </Typography>
          <Typography
            sx={{
              fontFamily: "var(--font-body)",
              color: "var(--hud-accent, #FFEE20)",
              fontSize: "9px",
              letterSpacing: "1px",
              textTransform: "none",
              cursor: "pointer",
              opacity: 0.6,
              transition: "opacity 0.2s ease",
              "&:hover": { opacity: 1 },
            }}
            onClick={() => {
              console.log("[SidebarRight] Going to => /app/feature/MostStreamedThisWeekStudio");
              history.push("/app/feature/MostStreamedThisWeekStudio");
            }}
          >
            Explore More
          </Typography>
        </Box>
        <List>
          {topPlaylists.map((playlist) => (
            <ListItem
              key={playlist._id}
              sx={{
                pl: 1.5,
                py: 0.8,
                borderRadius: "4px",
                backgroundColor: "transparent",
                border: "1px solid var(--hud-border, #16161F)",
                mb: 1,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "rgba(255, 238, 32,0.15)",
                  backgroundColor: "rgba(255, 238, 32,0.03)",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", width: "100%", cursor: "pointer" }} onClick={() => history.push(`/app/Music/${playlist._id}`)}>
                <Avatar
                  src={playlist.GIF?.filePath || playlist.thumbnailImg?.filePath || Music}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "4px",
                    mr: 1.5,
                    border: "1px solid var(--hud-border, #16161F)",
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontFamily: "var(--font-body)", fontSize: "12px", fontWeight: 600, color: "var(--hud-text, #E8E8EE)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {truncate(playlist.title, 15)}
                  </Typography>
                  <Typography sx={{ fontFamily: "var(--font-body)", fontSize: "10px", color: "var(--hud-text-muted, #9A9AB0)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {playlist.createdByDetails?.displayArtistName || (playlist.createdByDetails?.firstName ? `${playlist.createdByDetails.firstName} ${playlist.createdByDetails.lastName || ''}`.trim() : "Unknown Artist")}
                  </Typography>
                </Box>
                <Box sx={{
                  ml: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  minWidth: "fit-content"
                }}>
                  <Typography sx={{
                    fontFamily: "var(--font-body)",
                    fontSize: "13px",
                    color: "var(--hud-accent, #FFEE20)",
                    fontWeight: "bold",
                    lineHeight: 1.2
                  }}>
                    {Math.round(playlist.totalStreams || playlist.streamCount || 0)}
                  </Typography>
                  <Typography sx={{
                    fontFamily: "var(--font-body)",
                    fontSize: "8px",
                    color: "var(--hud-text-dim, #6A6A80)",
                    textTransform: "uppercase",
                    letterSpacing: "1px"
                  }}>
                    streams
                  </Typography>
                </Box>
              </Box>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* AdSense Display Ad — only render once the sidebar has real publisher content above it. */}
      {(topSongs.length > 0 || liveStreams.length > 0 || topPlaylists.length > 0 || featuredArtist) && (
        <Box mb={2} sx={{ borderRadius: '4px', overflow: 'hidden' }}>
          <AdSenseAd adSlot={AdSenseConfig.adSlots.sidebar} format="auto" responsive={true} />
        </Box>
      )}
    </Box>
  );
};

export default SidebarRight;
